/**
 * useTableData Hook
 *
 * Fetches table data for the Table Explorer using TanStack Query.
 * Uses Electron-owned Table Explorer providers for engine-specific fetching.
 */

import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createExecutionId } from '../../../../types/queryExecution';
import { fetchTableExplorerRows } from '../services/tableExplorerApi';
import { getResultCacheWindow } from '../../common/services/resultCacheApi';
import { usePopulationProgress, type PopulationProgressEntry } from '../../query-editor/hooks/usePopulationProgress';
import type { TableExplorerFetchRowsResult } from '../../../../types/tableExplorer';
import type { TableExplorerColumn, FilterGroup, SortDefinition } from '../../core/types/workspaceTypes';

type UseTableDataParams = {
  connectionId: string;
  database: string;
  schema: string;
  tableName: string;
  limit: number;
  /** Number of rows to skip for pagination (offset = (page - 1) * limit) */
  offset?: number;
  columns: string[] | '*';
  filters: FilterGroup[];
  sorting: SortDefinition[];
  enabled: boolean;
  /** PK columns to always include in query for edit mode */
  primaryKeyColumns?: string[];
  /** Additional columns to always include (e.g. for relationship exploration) */
  alwaysIncludeColumns?: string[];
  /**
   * Column names when columns='*' needs to be expanded into an explicit list
   * (e.g. SQLite rowid fallback, which is not included in SELECT *).
   */
  allColumnNames?: string[];
  /**
   * Tab id used as the result-cache `tabKey`. When set, paginating within a tab
   * reuses a single cache slot (the previous page is disposed by `executeQueryWithCache`
   * on each new fetch). When absent the IPC handler falls back to the per-execution id,
   * so the streaming UI still works but cross-page disposal is coarser.
   */
  tabId?: string;
};

type TableDataResult = {
  rows: Record<string, unknown>[];
  columns: TableExplorerColumn[];
  totalRowCount?: number;
  /**
   * APPROXIMATE whole-table count from catalog statistics, supplied by the
   * provider only when the exact `COUNT(*)` missed its budget on an unfiltered
   * fetch. `TableExplorerTab` persists it onto the tab content, where the
   * pagination model uses it (with a "≈" prefix) instead of falling all the way
   * back to has-more paging.
   */
  estimatedRowCount?: number;
  /** LOB column names (Oracle CLOB/NCLOB/BLOB/BFILE). BLOB hex previews are bounded, and on a windowed page every text cell longer than 1 KB is clipped — `fetchCellValue` fetches the full value. */
  lobColumns?: string[];
  /** Cache URI when the page spilled to a windowed source (see `resolveFetchedRows`). */
  cacheUri?: string;
  /** `'in-memory'` (rows came back inline) or `'windowed'` (rows were read back out of the cache). */
  populationMode?: 'in-memory' | 'windowed';
};

/** Injectable window reader — the real one is `resultCacheApi.getResultCacheWindow`. */
type WindowReader = (args: {
  uri: string;
  startIndex: number;
  count: number;
  expectedGeneration: number;
  expectedSourceEpoch?: number;
}) => Promise<{ rows: Record<string, unknown>[] } | undefined>;

/**
 * Resolve the rows for a settled fetch, reading them back out of the result
 * cache when the page was promoted to a windowed source.
 *
 * ## Why the whole page is read at once
 *
 * A Table Explorer page is bounded by the page-size picker (≤10K rows), so the
 * window we read is the page — there is no scroll position to window against and
 * nothing to page through inside it. The Query Editor's `useWindowedData` exists
 * because a QE result set can be millions of rows; a TE page cannot. Reading the
 * one page in a single `get-window` call therefore materializes exactly what the
 * *inline* (pre-promotion) path would have handed us for the same page — and
 * strictly less, because `result-cache:get-window` returns PREVIEW values (each
 * cell truncated at `PREVIEW_MAX_BYTES`), while the inline path carried the
 * untruncated ones. Memory is no worse than the small-page case that already
 * ships today.
 *
 * ## Why this lives inside the queryFn
 *
 * Doing the read here (rather than in a post-settle effect) keeps three
 * invariants for free:
 *  - **Rows and columns come from the same source.** Everything downstream —
 *    the display-source ladder, `tabContent.rows`, export, copy, the map pane —
 *    keeps reading one settled result and needs no windowed special case.
 *  - **No skeleton flicker.** The fetch stays `isFetching` until the rows are in
 *    hand, so the grid goes skeleton → rows instead of skeleton → "no rows" →
 *    rows.
 *  - **One settled result per page.** The read is issued by the same async task
 *    that produced the URI, so the rows it returns are the ones this fetch is
 *    cached under — enforced by `expectedSourceEpoch`, below.
 *
 * ## Lifecycle / disposal
 *
 * `executeQueryWithCache` disposes a tab's sources at the START of the tab's
 * NEXT execution, so a refetch (paging, filter, sort, refresh) that begins while
 * this read is still in flight can pull the source out from under it — and,
 * because the cache URI is deterministic per tab, put a DIFFERENT page's source
 * at the same URI. Both halves are handled, not prevented:
 *
 *  - disposed and not yet replaced — `handleGetWindow` answers a missing source
 *    with an empty window rather than throwing;
 *  - disposed and REPLACED — `expectedSourceEpoch` names the source instance
 *    this fetch created, and the handler answers a different instance exactly as
 *    it answers a missing one. Without it, page 1's read could be served page
 *    2's rows and React Query would cache them under page 1's key.
 *
 * Either way an empty window for a page the cache said had rows is reported as
 * an error instead of being cached as a successful empty page (which would leave
 * the user on an empty grid for the 30 s `staleTime`). React Query's single
 * retry then re-runs the fetch, which recreates the source. Nothing here
 * disposes anything — that would race the next fetch's own `disposeByPrefix`.
 */
export async function resolveFetchedRows(
  result: TableExplorerFetchRowsResult,
  args: { readonly limit: number; readonly readWindow: WindowReader },
): Promise<Record<string, unknown>[]> {
  if (result.populationMode !== 'windowed' || !result.cacheUri) return result.rows ?? [];

  // The cache holds this page and nothing else, so the whole source is the
  // window. Fall back to the requested page size when the count is missing.
  const expectedRowCount = result.cachedRowCount ?? args.limit;
  if (expectedRowCount <= 0) return [];

  const response = await args.readWindow({
    uri: result.cacheUri,
    startIndex: 0,
    count: expectedRowCount,
    // Sort/filter generation within the source. Table Explorer never sorts or
    // filters a cache source (it re-queries the database instead), so every
    // source it creates stays on generation 0 for its whole life.
    expectedGeneration: 0,
    // Which SOURCE INSTANCE this page's rows live in. The generation above
    // cannot say — a replacement source at the same URI also starts at 0 — so
    // this is what makes a read that lost the race to the next page's fetch
    // fail instead of returning that page's rows.
    expectedSourceEpoch: result.cacheSourceEpoch,
  });
  const rows = response?.rows ?? [];
  if (rows.length === 0) {
    throw new Error(
      'The rows for this page are no longer cached (the page was reloaded while they were being read). Refresh to load them again.',
    );
  }
  return rows;
}

export function buildEffectiveColumns(args: {
  readonly columns: string[] | '*';
  readonly primaryKeyColumns: string[];
  readonly alwaysIncludeColumns?: string[];
  readonly allColumnNames?: string[];
}): string[] | '*' {
  const extraColumns = args.alwaysIncludeColumns ?? [];

  if (args.columns === '*') {
    const requiresExplicitList = [...args.primaryKeyColumns, ...extraColumns].some((col) => {
      const normalized = col.toLowerCase();
      return normalized === 'rowid' || normalized === '_rowid_' || normalized === 'oid';
    });

    if (!requiresExplicitList) return '*';

    // If the *only* reason an explicit list would be required is a
    // synthesized SQLite rowid/_rowid_/oid PK (no other always-include
    // columns forcing expansion), keep `columns: '*'` — the SQLite query
    // generator inlines the rowid alias into `SELECT <alias> AS <alias>, *`
    // server-side (see sqliteQueryGenerator.syntheticRowidAliasFor). This
    // keeps the queryKey stable across the request that lands before table
    // metadata resolves and the one after: metadata resolving asynchronously
    // (which is what supplies this synthesized PK) would otherwise flip the
    // queryKey from '*' to an explicit list and force a duplicate refetch of
    // the same page once it lands.
    //
    // Note: this is a query-key-churn fix specific to the synthesized-rowid
    // case. A related but out-of-scope churn exists on the explicit-column
    // path: `alwaysIncludeColumns` for inbound-FK relationship exploration
    // can also arrive after the first fetch and change `effectiveColumns`,
    // triggering a refetch there too — left as-is for now.
    const isSoleSyntheticRowidPk =
      extraColumns.length === 0 &&
      args.primaryKeyColumns.length === 1 &&
      ['rowid', '_rowid_', 'oid'].includes(args.primaryKeyColumns[0].toLowerCase());
    if (isSoleSyntheticRowidPk) return '*';

    const baseColumns = args.allColumnNames ?? [];
    if (baseColumns.length === 0) return '*';

    const merged: string[] = [];
    const seen = new Set<string>();

    for (const required of [...args.primaryKeyColumns, ...extraColumns]) {
      if (seen.has(required)) continue;
      seen.add(required);
      merged.push(required);
    }

    for (const col of baseColumns) {
      if (seen.has(col)) continue;
      seen.add(col);
      merged.push(col);
    }

    return merged;
  }
  if (args.primaryKeyColumns.length === 0 && extraColumns.length === 0) return args.columns;

  const seen = new Set(args.columns);
  const merged = [...args.columns];
  for (const pkColumn of args.primaryKeyColumns) {
    if (seen.has(pkColumn)) continue;
    seen.add(pkColumn);
    merged.push(pkColumn);
  }
  for (const extraColumn of extraColumns) {
    if (seen.has(extraColumn)) continue;
    seen.add(extraColumn);
    merged.push(extraColumn);
  }
  return merged;
}

/**
 * Companion to the `isSoleSyntheticRowidPk` shortcut above: when
 * `buildEffectiveColumns` kept `columns: '*'` specifically because the sole
 * PK column is a synthesized SQLite rowid/_rowid_/oid alias, the *emitted
 * SQL* still depends on `primaryKeyColumns` — `sqliteQueryGenerator` inlines
 * `<alias> AS <alias>, *` once the alias is known — even though
 * `effectiveColumns` itself never changes shape. Table metadata (which
 * supplies this synthesized PK) resolves asynchronously and can still be
 * empty when the FIRST `table-data` fetch's queryFn runs (the query isn't
 * gated on metadata), so that fetch goes out as a plain `SELECT *` with no
 * rowid column at all. Once metadata resolves, `primaryKeyColumns` flips
 * from `[]` to `['rowid']` — this must be reflected in the queryKey (even
 * though `effectiveColumnsKey` stays `'*'` throughout) so TanStack Query
 * refetches and actually picks up the rowid column; otherwise the tab is
 * left with rowid-less cached rows forever, and edit mode (which builds its
 * WHERE clauses from `primaryKeyColumns`) silently updates zero rows. See
 * `useTableData.test.ts` for the regression this guards.
 */
export function syntheticRowidInlineKey(effectiveColumns: string[] | '*', primaryKeyColumns: string[]): string | null {
  if (effectiveColumns !== '*') return null;
  if (primaryKeyColumns.length !== 1) return null;
  const alias = primaryKeyColumns[0];
  return ['rowid', '_rowid_', 'oid'].includes(alias.toLowerCase()) ? alias : null;
}

/**
 * Hook to fetch table data using TanStack Query.
 *
 * Now also subscribes to the result-cache population-progress stream so consumers can show
 * a streaming UI while rows arrive. The progressive entry is exposed alongside the standard
 * `useQuery` return — existing callers reading `data.rows` are unaffected; new callers that
 * want the "rows-as-they-arrive" experience read `populationProgress.rows` while
 * `populationProgress.state === 'populating'`.
 */
export function useTableData(params: UseTableDataParams) {
  const {
    connectionId,
    database,
    schema,
    tableName,
    limit,
    offset = 0,
    columns,
    filters,
    sorting,
    enabled,
    primaryKeyColumns = [],
    alwaysIncludeColumns = [],
    allColumnNames = [],
    tabId,
  } = params;

  // Ensure PK columns are always included in the query for edit mode
  // This ensures we can always build WHERE clauses for updates/deletes.
  // Important: the query key should reflect the *actual* column list used in SQL.
  // Otherwise, when columns='*', PK discovery would trigger a redundant refetch.
  const effectiveColumns = buildEffectiveColumns({ columns, primaryKeyColumns, alwaysIncludeColumns, allColumnNames });

  // The set of selected columns affects the SQL projection; their *order* does
  // not (rows come back as name-keyed objects and the grid reorders them in
  // userspace). Sort the queryKey representation so a pure column reorder
  // doesn't invalidate the cache and trigger a refetch — that caused rows to
  // flash empty during drag-to-reorder.
  const effectiveColumnsKey: string[] | '*' = effectiveColumns === '*'
    ? '*'
    : [...effectiveColumns].sort();

  // See `syntheticRowidInlineKey` — captures the one case where the emitted
  // SQL depends on `primaryKeyColumns` despite `effectiveColumnsKey` staying
  // `'*'`, so the queryKey still changes (and refetches) when a synthesized
  // rowid PK becomes known after the first fetch already ran without it.
  const rowidInlineKey = syntheticRowidInlineKey(effectiveColumns, primaryKeyColumns);

  // Track the executionId of the in-flight fetch so consumers can cancel via `cancelQueryV2`.
  // Updated synchronously inside queryFn before the IPC round-trip starts.
  const currentExecutionIdRef = useRef<string | null>(null);
  // The same id as observable STATE. The ref alone cannot drive rendering: React
  // Query renders the new query key BEFORE the replacement `queryFn` runs (the
  // ref is written inside `queryFn`), and a ref mutation re-renders nothing — so
  // a ref-derived "Stopping…" kept showing the PREVIOUS fetch's cancel state
  // over a fetch that had received no cancel, until an unrelated render
  // refreshed it. The ref stays for the cancel call itself, which reads the id
  // synchronously inside a callback.
  const [activeExecutionId, setActiveExecutionId] = useState<string | null>(null);

  const query = useQuery<TableDataResult>({
    queryKey: ['table-data', connectionId, database, schema, tableName, limit, offset, effectiveColumnsKey, rowidInlineKey, filters, sorting, tabId],
    queryFn: async () => {
      const executionId = createExecutionId(`table-explorer-${tabId ?? tableName}`);
      currentExecutionIdRef.current = executionId;
      setActiveExecutionId(executionId);
      try {
        const result = await fetchTableExplorerRows({
          connectionId,
          executionId,
          tabId,
          table: { database, schema, tableName },
          columns: effectiveColumns,
          limit,
          offset,
          filters,
          sorting,
          primaryKeyColumns,
        });

        if (!result.success) {
          // Slice 6 — attach the structured error details onto the thrown Error
          // so `TableExplorerTab` can drive its connection-failure banner. Plain
          // string `error` is preserved as the message for back-compat with all
          // existing toasts/log paths.
          const tableDataError = new Error(result.error || 'Failed to fetch table data') as Error & {
            details?: { isDatabaseError?: boolean; sqlErrorCode?: number; originalMessage?: string };
          };
          if (result.errorDetails) tableDataError.details = result.errorDetails;
          throw tableDataError;
        }

        return {
          // In-memory pages arrive inline; a page that crossed the result-cache
          // promotion thresholds is read back out of its windowed source here.
          rows: await resolveFetchedRows(result, { limit, readWindow: getResultCacheWindow }),
          columns: result.columns || [],
          totalRowCount: result.totalRowCount,
          estimatedRowCount: result.estimatedRowCount,
          lobColumns: result.lobColumns,
          cacheUri: result.cacheUri,
          populationMode: result.populationMode,
        };
      } finally {
        // Drop the id the moment THIS fetch settles. `isFetching` only flips on
        // the next observer render, so a Stop clicked in that gap would
        // otherwise cancel (and emit analytics for) an execution that is
        // already finished. Guarded by the identity check: a refetch that
        // started while this one was settling has already claimed the ref, and
        // its id must survive.
        if (currentExecutionIdRef.current === executionId) currentExecutionIdRef.current = null;
        setActiveExecutionId((cur) => (cur === executionId ? null : cur));
      }
    },
    enabled: enabled && !!connectionId && !!tableName,
    staleTime: 30000, // Data is fresh for 30 seconds
    refetchOnWindowFocus: false,
  });

  // Subscribe to streaming progress for this tab. `isFetching` alone drives the
  // subscription effect inside the hook (it re-subscribes every time a fetch starts
  // and clears the accumulated entries every time one settles), so there is nothing
  // for a third argument to do here. TE always uses result-set index 0.
  //
  // `cacheGeneration` is deliberately NOT passed. It is a *staleness floor* — the hook
  // drops any event whose `generation` is below it — and it exists for the Query Editor,
  // which bumps a real per-execution counter on its tab content. Passing
  // `query.dataUpdatedAt` (an epoch-ms timestamp) put the floor at ~1.7e12 the moment the
  // first fetch settled, and TE's inline progress events carry `generation: 0`, so every
  // event of every later fetch was silently discarded: refresh / paging / filter / sort
  // showed a bare skeleton for the whole fetch instead of the rows streaming in.
  const populationProgressMap = usePopulationProgress(tabId, query.isFetching);
  const populationProgress: PopulationProgressEntry | undefined = populationProgressMap?.[0];
  const isPopulating = populationProgress?.state === 'populating';

  return {
    ...query,
    /** Live progress entry for this tab's single result set (undefined before first event). */
    populationProgress,
    /** True while batches are still arriving for the current fetch. */
    isPopulating,
    /** Ref to the executionId of the in-flight fetch — pass to `cancelQueryV2` to abort. */
    currentExecutionIdRef,
    /**
     * The same id as render-observable state (`null` when nothing is in flight).
     * Use this — not the ref — for anything the UI displays about the current
     * fetch, so a replacement fetch repaints instead of showing the old one's state.
     */
    activeExecutionId,
  };
}
